// Import React and useState hook to manage component state
import React, { useState } from "react";
// Import Link from react-router-dom for client-side navigation
import { Link } from "react-router-dom";
// Import FontAwesome brand icons for social media links
import { faFacebookF, faInstagram, faYoutube, } from "@fortawesome/free-brands-svg-icons";
// Import FontAwesome solid icons for menu toggles
import { faBars, faTimes, faAngleDown } from "@fortawesome/free-solid-svg-icons";
// Import FontAwesomeIcon component to render icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Import custom NavbarAd component (ad banner within navbar)
// import NavbarAd from "./NavbarAd";

// Navbar component receives an optional onSearch callback
const Navbar = ({ onSearch }) => {
  // Local state for search input value
  const [searchQuery, setSearchQuery] = useState("");
  // State to toggle mobile menu visibility
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // State to toggle top menu dropdown for smaller screens
  const [topMenuOpen, setTopMenuOpen] = useState(false);

  // Handle search input change
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);       // Update local state
    if (typeof onSearch === "function")  // Call callback if provided
      onSearch(e.target.value);
  };

  return (
    // Main header container with full width, shadow, small font
    <header className="w-full shadow text-sm font-sans">

      {/* Top Bar containing links and social icons */}
      <div className="bg-gray-900 text-white px-4 py-2 flex justify-between items-center text-sm">

        {/* Desktop Top Menu (hidden on small screens) */}
        <div className="hidden lg:flex text-xs gap-4 uppercase">
          <Link to="/" >Home</Link>
          <Link to="/advertise" className="hover:text-gray-400 transition">Advertise With Us</Link>
          <Link to="/contactus" className="hover:text-gray-400 transition">Contact Us</Link>
          <Link to="/privacypolicy" className="hover:text-gray-400 transition">Privacy Policy</Link>
          <Link
            to="https://store.newstarn.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-400 transition"
          >
            Store
          </Link>
        </div>

        {/* Top Menu Dropdown for mobile screens (< 1000px) */}
        <div className="lg:hidden text-xs relative">
          <button
            onClick={() => setTopMenuOpen(!topMenuOpen)}   // Toggle dropdown
            className="px-3 py-1 bg-gray-800 border border-gray-600 rounded flex items-center gap-2"
          >
            Menu <FontAwesomeIcon icon={faAngleDown} className="text-xs" />
          </button>
          {topMenuOpen && (
            // Dropdown menu items
            <div className="absolute left-0 mt-2 w-48 bg-white text-black rounded shadow z-20">
              <Link to="/" className="block px-4 py-2 hover:bg-gray-100">Home</Link>
              <Link to="/advertise" className="block px-4 py-2 hover:bg-gray-100">Advertise With Us</Link>
              <Link to="/contactus" className="block px-4 py-2 hover:bg-gray-100">Contact Us</Link>
              <Link to="/privacypolicy" className="block px-4 py-2 hover:bg-gray-100">Privacy Policy</Link>
              <Link to="https://store.newstarn.com/" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:bg-gray-100">Store</Link>
            </div>
          )}
        </div>

        {/* Social media icons */}
        <div className="flex gap-3 text-lg">
          <a href="https://facebook.com/people/Newstarn/61574926899598/" target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faFacebookF} className="hover:text-gray-400 transition" />
          </a>
          <a href="https://instagram.com/newstarnz/" target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faInstagram} className="hover:text-gray-400 transition" />
          </a>
          <a href="https://youtube.com/channel/UCmFWjs94o7yOrFYwwlT2geQ?sub_confirmation=1" target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faYoutube} className="hover:text-gray-400 transition" />
          </a>
        </div>
      </div>

      {/* Center Logo and Navbar Ad */}
      <div className="bg-white px-2 py-2 flex flex-col lg:flex-row justify-between items-left border-b">
        <div className="px-2 lg:mb-0 lg:mx-0">
          <div className="mb-1 px-2 lg:mb-0 mx-auto lg:mx-0">
            {/* Logo linking to homepage */}
            <Link to="/">
              <img
                src="/newstarn-logo.svg"
                alt="Newstarn Logo"
                style={{ width: "278px", height: "100px" }}
              />
            </Link>
          </div>
        </div>

        {/* Banner ad in navbar */}
        {/* <NavbarAd /> */}

      </div>

      {/* Main navigation bar with links and search */}
      <nav className="bg-gray-700 text-white px-4 pt-3 pb-3 md:pb-3 md:pt-3 relative">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 w-full">
            {/* <h1 className="text-xl lg:text-2xl font-bold tracking-wide uppercase mr-6 text-white">
              <Link to="/">Newstarn</Link>
            </h1> */}

            {/* Desktop navigation links */}
            <ul className="hidden lg:flex flex-wrap gap-4 text-base font-medium">
              <li><Link to="/sports" className="hover:text-gray-300">Sports</Link></li>
              <li><Link to="/technology" className="hover:text-gray-300">Technology</Link></li>
              <li><Link to="/business" className="hover:text-gray-300">Business</Link></li>
              <li><Link to="/health" className="hover:text-gray-300">Health</Link></li>
              <li><Link to="/politics" className="hover:text-gray-300">Politics</Link></li>
              <li><Link to="/entertainment" className="hover:text-gray-300">Entertainment</Link></li>
            </ul>
          </div>

          {/* Desktop search input (visible only on lg screens) */}
          <div className="relative hidden lg:block">
            <input
              id="search-filter"
              type="text"
              placeholder="Search news..."
              className="pl-4 pr-10 py-2 bg-white text-gray-700 w-64 text-sm shadow focus:outline-none focus:ring-2 focus:ring-gray-400"
              value={searchQuery}
              onChange={handleSearch}  // Update search state and call onSearch callback
            />
          </div>

          {/* Mobile menu toggle button */}
          <div className="lg:hidden self-end">
            <button
              className="text-white text-xl"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
            </button>
          </div>
        </div>

        {/* Mobile navigation menu (shown when mobileMenuOpen) */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 space-y-3">
            <ul className="flex flex-col gap-3 text-sm">
              <li><Link to="/sports" className="hover:text-gray-400 transition">Sports</Link></li>
              <li><Link to="/technology" className="hover:text-gray-400 transition">Technology</Link></li>
              <li><Link to="/business" className="hover:text-gray-400 transition">Business</Link></li>
              <li><Link to="/health" className="hover:text-gray-400 transition">Health</Link></li>
              <li><Link to="/politics" className="hover:text-gray-400 transition">Politics</Link></li>
              <li><Link to="/entertainment" className="hover:text-gray-400 transition">Entertainment</Link></li>
            </ul>

            {/* Mobile search input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search news..."
                className="pl-4 pr-10 py-2 bg-white text-gray-700 w-full text-sm shadow focus:outline-none focus:ring-2 focus:ring-gray-400"
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// Export Navbar component for use in other parts of the app
export default Navbar;
